'use client'

import { useEffect, useRef, useState } from 'react'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'

import ChatHeader from './ChatHeader'
import ChatInput from './ChatInput'
import MessageList from './MessageList'

interface Message {
  text: string
  isUser: boolean
  timestamp?: string
  patientProfilePicture?: string | null
}

interface WebSocketMessage {
  type?: string
  message?: string
  content?: string
  patient_profile_picture?: string | null
  chat_history?: { role: 'user' | 'assistant'; content: string }[]
  data?:
    | {
        message: string
        timestamp?: string
        sender?: string
      }[]
    | {
        message: string
        timestamp?: string
        sender?: string
      }
}

interface ChatbotProps {
  accessToken: string
}

const SOCKET_URL = `${process.env.NEXT_PUBLIC_SOCKET}/assessments/`

const Chatbot = ({ accessToken }: ChatbotProps) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [wsConnected, setWsConnected] = useState(false)
  const [isBotTyping, setIsBotTyping] = useState(false)
  const [isSpeechSupported, setIsSpeechSupported] = useState(true)
  const [patientProfilePicture, setPatientProfilePicture] = useState<string | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition()

  useEffect(() => {
    if (transcript) {
      setInputMessage(transcript)
    }
  }, [transcript])

  useEffect(() => {
    // Check if speech recognition is actually supported
    setIsSpeechSupported(browserSupportsSpeechRecognition)
  }, [browserSupportsSpeechRecognition])

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const startListening = () => {
    SpeechRecognition.startListening({ continuous: true })
  }

  const stopListening = () => {
    SpeechRecognition.stopListening()
    resetTranscript()
  }

  const handleWebSocketMessage = (data: WebSocketMessage) => {
    try {
      // Skip connection messages
      if (data.content === 'Connected to WebSocket server!') {
        return
      }

      console.log('WS handle', data)

      if ('patient_profile_picture' in data) {
        setPatientProfilePicture(data.patient_profile_picture ?? null)
      }

      // Handle top-level chat_history format: { chat_history: [{ role, content }] }
      if (Array.isArray(data.chat_history)) {
        setIsBotTyping(false)
        const formattedHistory: Message[] = data.chat_history
          .filter((msg) => msg && typeof msg.content === 'string' && typeof msg.role === 'string')
          .map((msg) => ({
            text: msg.content,
            isUser: msg.role === 'user',
            timestamp: new Date().toISOString(),
            patientProfilePicture: msg.role === 'user' ? (data.patient_profile_picture ?? patientProfilePicture) : null,
          }))
        setMessages(formattedHistory)
        if (data.chat_history.length === 0) {
          console.log('Chat cleared confirmed by server (empty chat_history)')
        } else {
          console.log('Chat history loaded', { count: data.chat_history.length })
        }
        return
      }

      // Handle message with content field
      if (typeof data.content === 'string') {
        setIsBotTyping(false)
        setMessages((prev) => [
          ...prev,
          {
            text: data.content as string,
            isUser: false,
            timestamp: new Date().toISOString(),
          },
        ])
        return
      }

      // Handle other message types if needed
      switch (data.type) {
        case 'chat_history':
          setIsBotTyping(false)
          if (data.data && Array.isArray(data.data)) {
            const formattedHistory = data.data
              .filter((msg) => msg && typeof msg.message === 'string')
              .map((msg) => ({
                text: msg.message,
                isUser: msg.sender === 'user',
                timestamp: msg.timestamp || new Date().toISOString(),
                patientProfilePicture:
                  msg.sender === 'user' ? (data.patient_profile_picture ?? patientProfilePicture) : null,
              }))
            setMessages(formattedHistory)
            console.log('Legacy chat history loaded', { count: formattedHistory.length })
          }
          break

        default:
          if (typeof data.message === 'string' && data.message !== 'Connected to WebSocket server!') {
            setIsBotTyping(false)
            setMessages((prev) => [
              ...prev,
              {
                text: data.message as string,
                isUser: false,
                timestamp: new Date().toISOString(),
              },
            ])
          }
      }
    } catch (error) {
      setIsBotTyping(false)
      console.error('Error handling WebSocket message:', error)
    }
  }

  useEffect(() => {
    // Initialize WebSocket connection with token
    const wsUrl = `${SOCKET_URL}?token=${accessToken}`
    const ws = new WebSocket(wsUrl)

    // Add token to WebSocket headers if needed
    ws.onopen = () => {
      console.log('WebSocket connected', { url: wsUrl })
      // ws.send(
      //   JSON.stringify({
      //     type: 'authentication',
      //     token: accessToken,
      //   })
      // )
      setWsConnected(true)
    }

    wsRef.current = ws

    ws.onmessage = (event) => {
      try {
        const data: WebSocketMessage = JSON.parse(event.data)
        console.log('WS recv', data)
        handleWebSocketMessage(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    ws.onclose = () => {
      console.log('WebSocket closed')
      setWsConnected(false)
    }

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error)
    }

    // Cleanup on component unmount
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, [accessToken])

  const sendWebSocketMessage = (message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      setIsBotTyping(true)
      const messageData: WebSocketMessage = {
        type: 'message',
        message: message,
        data: {
          message: message,
          timestamp: new Date().toISOString(),
        },
      }
      wsRef.current.send(JSON.stringify(messageData))
    } else {
      console.error('WebSocket is not connected')
    }
  }

  const handleSendMessage = () => {
    if (inputMessage.trim() && wsConnected) {
      // Add user message to local state
      const newMessage = {
        text: inputMessage,
        isUser: true,
        timestamp: new Date().toISOString(),
        patientProfilePicture,
      }
      setMessages((prev) => [...prev, newMessage])

      // Send message through WebSocket
      sendWebSocketMessage(inputMessage)

      setInputMessage('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`
    }
  }

  useEffect(() => {
    adjustTextareaHeight()
  }, [inputMessage])

  const clearChat = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const payload = JSON.stringify({
        type: 'message',
        data: { action: 'clear_chat' },
      })
      console.log('WS send', payload)
      wsRef.current.send(payload)
      setMessages([])
    }
  }

  return (
    <div
      className={`
        h-[600px] w-full

        md:w-2/5
      `}
    >
      <div
        className={`
          flex h-full flex-col justify-between overflow-hidden rounded-xl
          shadow-xl
        `}
      >
        <ChatHeader wsConnected={wsConnected} onClearChat={clearChat} />
        <MessageList messages={messages} isBotTyping={isBotTyping} messagesContainerRef={messagesContainerRef} />
        <ChatInput
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          handleSendMessage={handleSendMessage}
          handleKeyPress={handleKeyPress}
          textareaRef={textareaRef}
          wsConnected={wsConnected}
          listening={listening}
          startListening={startListening}
          stopListening={stopListening}
          isSpeechSupported={isSpeechSupported}
        />
      </div>
    </div>
  )
}

export default Chatbot
